import { createReducer, on } from '@ngrx/store';

import {
  appendErrorPart,
  applyConsentDecisionToToolCallPart,
  applyStreamFrames,
  attachConsentToToolCallPart,
  finalizeActivePart,
} from '@domain/ai-assistant/ai-assistant-stream.accumulator';
import { PendingToolConsent } from '@domain/ai-assistant/ai-assistant-stream.models';
import { mapHistoryToMessageVms } from '@domain/ai-assistant/ai-assistant.mappings';
import { AiAssistantPromptPreset, AiConversationStatus, ConversationSummary } from '@domain/ai-assistant/ai-assistant.models';
import { AiMessageVm } from '@domain/ai-assistant/ai-assistant.view-models';

import {
  clearAiAssistant,
  closePanel,
  createConversation,
  createConversationFailure,
  createConversationSuccess,
  ensurePromptPresetsLoaded,
  loadConversationMessages,
  loadConversationMessagesFailure,
  loadConversationMessagesSuccess,
  loadConversations,
  loadConversationsFailure,
  loadConversationsSuccess,
  loadPromptPresets,
  loadPromptPresetsFailure,
  loadPromptPresetsSuccess,
  loadRecentConversations,
  loadRecentConversationsFailure,
  loadRecentConversationsSuccess,
  openPanel,
  resetAiAssistant,
  selectConversation,
  sendConsentDecision,
  sendMessage,
  setPanelSize,
  startNewConversation,
  stopStream,
  streamDone,
  streamErrored,
  streamFramesReceived,
  streamStarted,
  streamWaitingForConsent,
} from './ai-assistant.actions';

const INITIAL_CONVERSATIONS_TOTAL = 0;

export const aiAssistantFeatureKey = 'aiAssistant';

export interface AiAssistantState {
  panelSize: 'closed' | 'small' | 'fullscreen';
  conversations: ConversationSummary[];
  conversationsLoading: boolean;
  conversationsTotal: number;
  conversationsError?: string;
  recentConversations: ConversationSummary[];
  recentConversationsLoading: boolean;
  promptPresets?: AiAssistantPromptPreset[];
  promptPresetsLoading: boolean;
  promptPresetsError?: string;
  activeConversationId?: string;
  status: AiConversationStatus;
  messages: AiMessageVm[];
  messagesLoading: boolean;
  messagesError?: string;
  isStreaming: boolean;
  activePartId?: string;
  streamError?: string;
  pendingConsent?: PendingToolConsent;
  creatingConversation: boolean;
}

const initialState: AiAssistantState = {
  panelSize: 'closed',
  conversations: [],
  conversationsLoading: false,
  conversationsTotal: INITIAL_CONVERSATIONS_TOTAL,
  recentConversations: [],
  recentConversationsLoading: false,
  promptPresetsLoading: false,
  status: 'IDLE',
  messages: [],
  messagesLoading: false,
  isStreaming: false,
  creatingConversation: false,
};

export const aiAssistantReducer = createReducer(
  initialState,

  // ---------- Panel ----------

  on(openPanel, (state, { size }): AiAssistantState => ({ ...state, panelSize: size })),
  on(setPanelSize, (state, { size }): AiAssistantState => ({ ...state, panelSize: size })),
  on(closePanel, (state): AiAssistantState => ({ ...state, panelSize: 'closed' })),

  // ---------- Conversations ----------

  on(
    loadConversations,
    (state, { offset }): AiAssistantState => ({
      ...state,
      conversationsLoading: true,
      conversationsError: undefined,
      conversations: offset ? state.conversations : [],
    })
  ),
  on(
    loadConversationsSuccess,
    (state, { conversations, total, offset }): AiAssistantState => ({
      ...state,
      conversations: offset ? [...state.conversations, ...conversations] : conversations,
      conversationsTotal: total,
      conversationsLoading: false,
    })
  ),
  on(
    loadConversationsFailure,
    (state, { error }): AiAssistantState => ({
      ...state,
      conversationsLoading: false,
      conversationsError: error,
    })
  ),

  on(
    loadRecentConversations,
    (state): AiAssistantState => ({
      ...state,
      recentConversationsLoading: true,
    })
  ),
  on(
    loadRecentConversationsSuccess,
    (state, { conversations }): AiAssistantState => ({
      ...state,
      recentConversations: conversations,
      recentConversationsLoading: false,
    })
  ),
  on(
    loadRecentConversationsFailure,
    (state): AiAssistantState => ({
      ...state,
      recentConversationsLoading: false,
    })
  ),

  on(
    ensurePromptPresetsLoaded,
    (state): AiAssistantState =>
      state.promptPresets !== undefined || state.promptPresetsLoading ? state : { ...state, promptPresetsError: undefined }
  ),
  on(loadPromptPresets, (state): AiAssistantState => ({ ...state, promptPresetsLoading: true, promptPresetsError: undefined })),
  on(loadPromptPresetsSuccess, (state, { promptPresets }): AiAssistantState => ({ ...state, promptPresets, promptPresetsLoading: false })),
  on(
    loadPromptPresetsFailure,
    (state, { error }): AiAssistantState => ({ ...state, promptPresetsLoading: false, promptPresetsError: error })
  ),

  on(
    startNewConversation,
    (state): AiAssistantState => ({
      ...state,
      activeConversationId: undefined,
      messages: [],
      activePartId: undefined,
      streamError: undefined,
      pendingConsent: undefined,
      status: 'IDLE',
      isStreaming: false,
    })
  ),

  on(createConversation, (state): AiAssistantState => ({ ...state, creatingConversation: true })),
  on(
    createConversationSuccess,
    (state, { conversationId }): AiAssistantState => ({
      ...state,
      creatingConversation: false,
      activeConversationId: conversationId,
      messages: [],
      activePartId: undefined,
      streamError: undefined,
      pendingConsent: undefined,
      status: 'IDLE',
    })
  ),
  on(
    createConversationFailure,
    (state): AiAssistantState => ({
      ...state,
      creatingConversation: false,
    })
  ),

  on(
    selectConversation,
    (state, { conversationId }): AiAssistantState => ({
      ...state,
      activeConversationId: conversationId,
      messages: [],
      activePartId: undefined,
      streamError: undefined,
      pendingConsent: undefined,
      status: 'IDLE',
    })
  ),
  on(
    loadConversationMessages,
    (state): AiAssistantState => ({
      ...state,
      messagesLoading: true,
      messagesError: undefined,
      messages: [],
    })
  ),
  on(
    loadConversationMessagesSuccess,
    (state, { status, messages }): AiAssistantState => ({
      ...state,
      messagesLoading: false,
      status,
      messages: mapHistoryToMessageVms(messages),
    })
  ),
  on(
    loadConversationMessagesFailure,
    (state, { error }): AiAssistantState => ({
      ...state,
      messagesLoading: false,
      messagesError: error,
    })
  ),

  // ---------- Streaming ----------

  on(
    sendMessage,
    (state, { message }): AiAssistantState => ({
      ...state,
      messages: [
        ...state.messages,
        {
          id: crypto.randomUUID(),
          role: 'user',
          parts: [{ id: crypto.randomUUID(), kind: 'output', complete: true, text: message }],
          timestamp: new Date().toISOString(),
        },
      ],
    })
  ),

  on(sendConsentDecision, (state, { consentId, decision }): AiAssistantState => {
    const messages = applyConsentDecisionToToolCallPart(state.messages, consentId, decision);
    return { ...state, messages, pendingConsent: undefined };
  }),

  on(
    streamStarted,
    (state): AiAssistantState => ({
      ...state,
      isStreaming: true,
      streamError: undefined,
      status: 'RUNNING',
    })
  ),

  on(streamFramesReceived, (state, { conversationId, frames }): AiAssistantState => {
    if (state.activeConversationId !== conversationId) return state;
    const { messages, activePartId } = applyStreamFrames(state.messages, state.activePartId, frames);
    return { ...state, messages, activePartId };
  }),

  on(streamDone, (state, { conversationId }): AiAssistantState => {
    if (state.activeConversationId !== conversationId) return state;
    const messages = finalizeActivePart(state.messages, state.activePartId);
    return {
      ...state,
      messages,
      activePartId: undefined,
      isStreaming: false,
      status: 'IDLE',
    };
  }),

  on(streamWaitingForConsent, (state, { conversationId, pendingToolConsent }): AiAssistantState => {
    if (state.activeConversationId !== conversationId) return state;
    const messages = attachConsentToToolCallPart(state.messages, state.activePartId, pendingToolConsent);
    const finalizedMessages = finalizeActivePart(messages, state.activePartId);
    return {
      ...state,
      messages: finalizedMessages,
      activePartId: undefined,
      isStreaming: false,
      status: 'WAITING_FOR_TOOL_CONSENT',
      pendingConsent: pendingToolConsent,
    };
  }),

  on(streamErrored, (state, { conversationId, message }): AiAssistantState => {
    if (state.activeConversationId !== conversationId) return state;
    const messages = appendErrorPart(finalizeActivePart(state.messages, state.activePartId), message);
    return {
      ...state,
      messages,
      activePartId: undefined,
      isStreaming: false,
      streamError: message,
      status: 'IDLE',
    };
  }),

  on(
    stopStream,
    (state): AiAssistantState => ({
      ...state,
      isStreaming: false,
    })
  ),

  // ---------- Cross-cutting ----------

  on(
    clearAiAssistant,
    (state): AiAssistantState => ({
      ...initialState,
      // Prompt presets are cached for the app lifecycle.
      promptPresets: state.promptPresets,
      promptPresetsLoading: state.promptPresetsLoading,
      promptPresetsError: state.promptPresetsError,
    })
  ),

  on(resetAiAssistant, (): AiAssistantState => initialState)
);
